import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Footer from "../components/Footer";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/Daily-Routine-1.svg";
import WhatsAppButton from "../components/WhatsappButton";

const EmotionalSafety: React.FC = () => {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: "🌟",
      title: t("emotionalSafety.benefits.dareExplore.title"),
      description: t("emotionalSafety.benefits.dareExplore.description"),
    },
    {
      icon: "💪",
      title: t("emotionalSafety.benefits.emotionalRegulation.title"),
      description: t(
        "emotionalSafety.benefits.emotionalRegulation.description"
      ),
    },
    {
      icon: "😊",
      title: t("emotionalSafety.benefits.positiveSelf.title"),
      description: t("emotionalSafety.benefits.positiveSelf.description"),
    },
    {
      icon: "🤝",
      title: t("emotionalSafety.benefits.lastingRelationships.title"),
      description: t(
        "emotionalSafety.benefits.lastingRelationships.description"
      ),
    },
  ];

  const practices = [
    {
      title: t("emotionalSafety.practices.familiarFaces.title"),
      description: t("emotionalSafety.practices.familiarFaces.description"),
      icon: "👥",
      color: "blue",
      dailyStructure: t(
        "emotionalSafety.practices.familiarFaces.dailyStructure"
      ),
      dailyStructureDesc: t(
        "emotionalSafety.practices.familiarFaces.dailyStructureDesc"
      ),
    },
    {
      title: t("emotionalSafety.practices.individualAttention.title"),
      description: t(
        "emotionalSafety.practices.individualAttention.description"
      ),
      icon: "👁️",
      color: "green",
      staffApproach: t(
        "emotionalSafety.practices.individualAttention.staffApproach"
      ),
      approaches: [
        t("emotionalSafety.practices.individualAttention.approaches.eyeLevel"),
        t(
          "emotionalSafety.practices.individualAttention.approaches.acknowledgeFeelings"
        ),
        t(
          "emotionalSafety.practices.individualAttention.approaches.neverReject"
        ),
        t(
          "emotionalSafety.practices.individualAttention.approaches.clearBoundaries"
        ),
      ],
    },
    {
      title: t("emotionalSafety.practices.parentalTrust.title"),
      description: t("emotionalSafety.practices.parentalTrust.description"),
      icon: "🤗",
      color: "purple",
      parentPartnership: t(
        "emotionalSafety.practices.parentalTrust.parentPartnership"
      ),
      partnerships: [
        t("emotionalSafety.practices.parentalTrust.partnerships.warmHandover"),
        t(
          "emotionalSafety.practices.parentalTrust.partnerships.openCommunication"
        ),
        t("emotionalSafety.practices.parentalTrust.partnerships.takeConcerns"),
        t(
          "emotionalSafety.practices.parentalTrust.partnerships.involveParents"
        ),
      ],
    },
  ];

  const staffExpectations = [
    {
      icon: "🔄",
      title: t("emotionalSafety.staffExpectations.selfReflective.title"),
      description: t(
        "emotionalSafety.staffExpectations.selfReflective.description"
      ),
    },
    {
      icon: "🎓",
      title: t("emotionalSafety.staffExpectations.emotionalTraining.title"),
      description: t(
        "emotionalSafety.staffExpectations.emotionalTraining.description"
      ),
    },
    {
      icon: "🧠",
      title: t(
        "emotionalSafety.staffExpectations.developmentalKnowledge.title"
      ),
      description: t(
        "emotionalSafety.staffExpectations.developmentalKnowledge.description"
      ),
    },
    {
      icon: "⏰",
      title: t("emotionalSafety.staffExpectations.timeToConnect.title"),
      description: t(
        "emotionalSafety.staffExpectations.timeToConnect.description"
      ),
    },
    {
      icon: "👀",
      title: t("emotionalSafety.staffExpectations.visiblySensitive.title"),
      description: t(
        "emotionalSafety.staffExpectations.visiblySensitive.description"
      ),
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      {/* Hero Section */}
      <div className="relative z-10">
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 hero-section-h">
          {/* Decorative SVGs (scaled with container) */}
          <div className="absolute inset-0 pointer-events-none z-0">
            {/* Left SVG */}
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full opacity-100"
            />
            {/* Right SVG */}
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-[-60px] right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[320px] w-full opacity-100"
            />
          </div>
          <div className="max-w-6xl mx-auto z-10 relative">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-child font-semibold text-black mb-6 leading-tight">
                {t("emotionalSafety.title")}
              </h2>
              <p className="text-xl md:text-2xl text-neutral-800 mb-10 max-w-4xl mx-auto leading-relaxed">
                {t("emotionalSafety.subtitle")}
              </p>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-purple-50 rounded-3xl p-8 shadow-soft">
              <h2 className="text-3xl font-child font-semibold text-black mb-6">
                {t("emotionalSafety.title")}
              </h2>
              <p className="text-lg text-neutral-800 leading-relaxed mb-6">
                {t("emotionalSafety.introText1")}
              </p>
              <p className="text-lg text-neutral-800 leading-relaxed">
                {t("emotionalSafety.introText2")}
              </p>
            </div>
          </div>
        </section>

        {/* Why Emotional Safety is Essential */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-12 text-center">
              {t("emotionalSafety.whyEssential")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl p-6 shadow-soft hover:shadow-warm transition-all duration-300 transform hover:scale-105"
                >
                  <div className="text-4xl mb-4 text-center">
                    {benefit.icon}
                  </div>
                  <h3 className="text-lg font-child font-semibold text-black mb-3 text-center">
                    {benefit.title}
                  </h3>
                  <p className="text-neutral-800 text-center leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* In Practice */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-12 text-center">
              {t("emotionalSafety.inPractice")}
            </h2>

            <div className="space-y-8">
              {practices.map((practice, index) => (
                <div
                  key={index}
                  className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft hover:shadow-warm transition-all duration-300"
                >
                  <div className="flex items-start md:space-x-6">
                    <div className="flex-shrink-0">
                      <div
                        className={`w-20 h-20 rounded-2xl hidden md:flex items-center justify-center text-4xl bg-${practice.color}-200`}
                      >
                        {practice.icon}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-child font-semibold text-black mb-4">
                        {practice.title}
                      </h3>
                      <p className="text-lg text-neutral-800 leading-relaxed mb-6">
                        {practice.description}
                      </p>

                      {index === 0 && (
                        <div className="bg-blue-50 rounded-2xl p-6">
                          <h4 className="text-lg font-child font-semibold text-blue-800 mb-3">
                            {practice.dailyStructure}
                          </h4>
                          <p className="text-neutral-800 leading-relaxed">
                            {practice.dailyStructureDesc}
                          </p>
                        </div>
                      )}

                      {index === 1 && (
                        <div className="bg-green-50 rounded-2xl p-6">
                          <h4 className="text-lg font-child font-semibold text-green-800 mb-3">
                            {practice.staffApproach}
                          </h4>
                          <ul className="pl-5 space-y-2 text-neutral-800 marker:text-green-500">
                            {practice.approaches?.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {index === 2 && (
                        <div className="bg-purple-50 rounded-2xl p-6">
                          <h4 className="text-lg font-child font-semibold text-purple-800 mb-3">
                            {practice.parentPartnership}
                          </h4>
                          <ul className="pl-5 space-y-2 text-neutral-800 marker:text-purple-500">
                            {practice.partnerships?.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Training and Development */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 ">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-12 text-center">
              {t("emotionalSafety.trainingDevelopment")}
            </h2>

            <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft">
              <p className="text-lg text-neutral-800 leading-relaxed mb-8">
                {t("emotionalSafety.trainingIntro")}
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {staffExpectations.map((expectation, index) => (
                  <div
                    key={index}
                    className="bg-orange-50 rounded-2xl p-6 shadow-soft"
                  >
                    <div className="text-3xl mb-4 text-center">
                      {expectation.icon}
                    </div>
                    <h4 className="text-lg font-child font-semibold text-black mb-3 text-center">
                      {expectation.title}
                    </h4>
                    <p className="text-neutral-800 text-center leading-relaxed">
                      {expectation.description}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 rounded-2xl p-6">
                <h4 className="text-3xl md:text-xl font-child font-semibold text-black mb-4">
                  {t("emotionalSafety.continuousDevelopment")}
                </h4>
                <p className="text-neutral-800 leading-relaxed">
                  {t("emotionalSafety.continuousDevDesc")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Creating Safe Spaces */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-12 text-center">
              {t("emotionalSafety.creatingSafeSpaces")}
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-green-50 rounded-3xl p-8 shadow-soft">
                <div className="w-full h-48 bg-green-200 rounded-2xl mb-6 flex items-center justify-center">
                  <span className="text-6xl">🏠</span>
                </div>
                <h3 className="text-2xl font-child font-semibold text-black mb-4">
                  {t("emotionalSafety.secureEnvironment.title")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("emotionalSafety.secureEnvironment.description")}
                </p>
              </div>

              <div className="bg-purple-50 rounded-3xl p-8 shadow-soft">
                <div className="w-full h-48 bg-purple-200 rounded-2xl mb-6 flex items-center justify-center">
                  <span className="text-6xl">💝</span>
                </div>
                <h3 className="text-2xl font-child font-semibold text-black mb-4">
                  {t("emotionalSafety.emotionalConnection.title")}
                </h3>
                <p className="text-neutral-800 leading-relaxed">
                  {t("emotionalSafety.emotionalConnection.description")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        {/* <section className="py-20 px-4 sm:px-6 lg:px-8 ">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-child font-semibold text-black mb-6">
              {t("emotionalSafety.experienceEnvironment")}
            </h2>
            <p className="text-xl text-neutral-600 mb-8 leading-relaxed">
              {t("emotionalSafety.experienceSubtitle")}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                className="inline-flex items-center justify-center px-8 py-3 bg-[#FBEACD] text-gray-900 font-child font-semibold text-lg rounded-full shadow-warm hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
              >
                {t("emotionalSafety.cta.scheduleVisit")}
              </button>
              <button
                className="inline-flex items-center justify-center px-8 py-3 bg-[#FBEACD] text-gray-900 font-child font-semibold text-lg rounded-full border-2 hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 transform hover:scale-105"
                style={{ borderColor: "#492A19" }}
              >
                {t("emotionalSafety.cta.learnMore")}
              </button>
            </div>
          </div>
        </section> */}
      </div>

      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default EmotionalSafety;
